/**
 * The `studio` namespace, in English — copy about **the firm itself**: B2, its own people
 * (`KLQ-20260819-006`), and since 2026-08-27 the standards it works to (`KLQ-20260827-001`).
 *
 * Its own namespace rather than more of `people`, per the rule in `lib/i18n/catalog.ts`:
 * namespaces are split by screen family, and *who works here* is a different family from
 * *who is on this project*. That split is the same one the product is selling, so collapsing
 * it in the copy would be the first place the distinction blurs. The norms section is in that
 * same family for the same reason — *what rules does this firm work under* is a fact about the
 * studio, edited from the same settings screen, and a language pack that ships one of the two
 * should ship the other.
 *
 * **`role.*.effect` is the load-bearing copy on this screen.** The workspace role decides
 * whether a colleague sees every project the firm owns or only the ones they are put on —
 * `core/authz.py:with_authority`, where `member` maps to level `NONE`. That consequence is
 * invisible in the field label, and a field whose effect is invisible is a field that gets
 * set wrong. TWIN: `with_authority`'s two disjuncts. If the arithmetic changes, these three
 * sentences are wrong and nothing will fail.
 */

export const studioEn = {
  'page.title': 'People in your studio',
  'page.subtitle': 'Everybody who works here, and what each of them can see.',

  'action.invite': 'Add person',
  'action.revoke': 'Withdraw',

  'status.loading': 'Loading your studio…',

  'group.members': 'Members',
  'group.count': '{count, plural, one {# person} other {# people}}',
  'group.pending': 'Invited, not yet joined',
  'group.pendingHint': 'These people have been emailed a link and have not used it yet.',

  'row.you': 'You',
  // A membership whose account has been erased. The row survives so the list still explains
  // who holds which authority, without naming an erased person.
  'row.redacted': 'Removed account',
  'row.roleLabel': 'Role of {name}',
  'row.remove': 'Remove {name} from the studio',
  'row.withdraw': 'Withdraw the invitation to {name}',
  'row.expires': 'Expires {date}',
  'row.expired': 'Expired',
  'row.joined': 'Joined {date}',

  'role.admin': 'Admin',
  'role.manager': 'Manager',
  'role.member': 'Member',
  'role.admin.effect': 'Full control of the studio. Sees every project.',
  'role.manager.effect': 'Sees every project the studio owns, and can put people on them.',
  'role.member.effect': 'Sees only the projects they are put on.',

  'empty.explains': 'Studio colleagues work on its projects. Their role decides what they see.',
  'empty.title': 'It is just you so far',
  'empty.body':
    'Add the people you work with. They get an email, and what they can see depends on the role you give them.',

  'invite.title': 'Add somebody to the studio',
  'invite.subtitle':
    'They get an email with a link. Using it creates their account inside your studio, not a company of their own.',
  'invite.send': 'Send invitation',
  'invite.field.email.label': 'Email address',
  'invite.field.email.placeholder': 'kolega@studio.example',
  'invite.field.role.label': 'Role in the studio',
  'invite.field.role.hint': 'Projects are separate: the same person can be an architect on one and a contractor on another.',
  'invite.error.emailRequired': 'Enter the email address to invite.',
  'invite.error.alreadyInvited': 'That person already works here or has already been invited.',
  // The temporary one. `KLQ-20260819-007` is what removes it, so the sentence says what is
  // true today rather than pretending the address is unusable.
  'invite.error.accountExists':
    'That address already has a Kalqa account. Somebody who already uses Kalqa cannot join a second studio yet.',
  'invite.error.verifyEmail':
    'Your studio has not confirmed its own email address yet, so it cannot send invitations.',
  'invite.error.verifyDomain':
    'Your studio has reached today’s limit for emailing people it has not contacted before. Verifying your domain raises it.',
  'invite.error.quota': 'A sending limit on your studio prevents this invitation right now.',

  'drawer.close': 'Close',
  'drawer.cancel': 'Cancel',

  // --- The admin door (`KLQ-20260821-002` §E7) --------------------------------
  //
  // Two acts for a colleague who cannot get in. The reset link is the ordinary one; the
  // direct set is for the phone call, and §E5 is explicit that it opens a window this
  // product cannot close — so the copy says so, in the panel, before the field. The four
  // `warning.*` sentences are load-bearing: three of them are consequences the server
  // guarantees, and the fourth is the one nothing can take away.
  //
  // `row.credentials.*` is the other half of the same honesty. A control missing from one
  // row and present on the next reads as a bug unless the row says why.
  'row.credentials.external':
    'This person’s account belongs to another studio, so their password is not yours to change.',
  'row.credentials.peer':
    'You cannot change the password of somebody who holds the same authority as you, or more.',

  'credentials.action.reset': 'Send a password reset',
  'credentials.action.set': 'Set a password',

  'credentials.reset.title': 'Send {name} a reset link?',
  'credentials.reset.body':
    'They get an email with a link to choose a new password. Nothing changes until they use it, and their current password keeps working until they do.',
  'credentials.reset.confirm': 'Send the link',

  'credentials.notice.reset': 'A link to choose a new password is on its way to {name}.',
  'credentials.notice.set':
    'Done. {name} has been signed out everywhere and told by email. Ask them to change it.',

  'credentials.set.title': 'Set a password for {name}',
  'credentials.set.subtitle':
    'For a colleague who cannot get into their email either. You are about to read them a password out loud.',
  'credentials.set.warning.title': 'Before you do this',
  'credentials.set.warning.signedOut': '{name} is signed out of Kalqa everywhere, immediately.',
  'credentials.set.warning.emailed':
    '{name} is told by email that you did it. No setting can switch that message off.',
  'credentials.set.warning.audited': 'It goes into the audit log, with your name on it.',
  'credentials.set.warning.window':
    'You will know a working password for {name} until they change it. Until they do, anything done with their account is recorded as them.',
  'credentials.set.field.label': 'The password you will read out',
  'credentials.set.field.hint':
    'At least {min} characters. Reveal it while you read it out, then close this panel.',
  'credentials.set.field.show': 'Show the password',
  'credentials.set.field.hide': 'Hide the password',
  'credentials.set.submit': 'Set the password',

  'credentials.instead.body':
    'If they can read their email, send a link instead. Then nobody but they ever knows the password.',
  'credentials.instead.action': 'Send a reset link instead',

  'credentials.error.forbidden':
    'You cannot do that for this person. They may hold the same authority as you, or your own role changed while this page was open.',
  'credentials.error.gone':
    'That person is no longer somebody you can do this for. They may have left, or their account may belong to another studio.',
  'credentials.error.rateLimited':
    'Your studio has used up its password actions for now. Try again later.',
  'credentials.error.rateLimitedIn':
    'Your studio has used up its password actions for now. Try again in {minutes, plural, one {# minute} other {# minutes}}.',
  'credentials.error.required': 'Type the password you are going to set.',
  'credentials.error.tooShort': 'A password needs at least {min} characters.',
  'credentials.error.tooLong': 'That password is longer than {max} characters.',
  'credentials.error.unexpected': 'Something went wrong. Nothing was changed. Try again.',

  'error.load': 'Your studio’s people could not be loaded.',
  'error.forbidden': 'You do not have permission to do that.',
  'error.gone': 'That person or invitation is no longer here.',
  'error.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'error.validation': 'Something in that request was not accepted.',
  'error.lastAdmin': 'A studio needs at least one admin. Promote somebody else first.',
  'error.unexpected': 'Something went wrong. Try again.',

  // --- The standards this firm works to (`KLQ-20260827-001`) ------------------
  //
  // In the `studio` namespace and not in `settings`, because the namespace is the *firm*
  // rather than one screen: who works here, and what body of rules the work is done under.
  // Both are answers about the studio itself, both are edited from Studio settings, and a
  // language pack that ships one should ship the other.
  'norms.title': 'Standards this studio works to',
  'norms.subtitle':
    'A project is planned in the phases its standard defines. Tick every one your office works to; each project then picks from them.',
  'norms.loading': 'Loading standards…',
  'norms.default.label': 'New projects start on',
  'norms.default.none': 'Ask each time',
  'norms.default.hint':
    'Only a starting point. Every project can be set to any of the standards ticked above.',
  'norms.badge.default': 'Default',
  'norms.badge.unavailable': 'Not yet',
  'norms.unavailable.why': 'We have not published the phases for this standard yet.',
  'norms.phaseCount': '{label} · {count, plural, one {# phase} other {# phases}}',
  'norms.readOnly': 'Only an admin or a manager can change this list.',
  'norms.action.save': 'Save standards',
  'norms.action.cancel': 'Discard changes',

  // The refusal this endpoint exists for. The count is ours; the project names come from the
  // server's own sentence, which is the only place they exist (`features/norms/problems.ts`).
  'norms.conflict.title':
    '{count, plural, one {One project still uses that standard} other {# projects still use that standard}}',

  'norms.error.load': 'The list of standards could not be loaded.',
  'norms.error.forbidden': 'You do not have permission to change the studio’s standards.',
  'norms.error.gone': 'That standard is no longer in the catalogue.',
  'norms.error.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'norms.error.validation': 'Something in that request was not accepted.',
  'norms.error.noLadder':
    'One of those standards has no published phases yet, so it cannot be switched on.',
  'norms.error.unexpected': 'Something went wrong. Try again.',
  // The firm directory as its own screen (`KLQ-20260820-007`, owner 2026-09-16).
  'firms.title': 'Firms we work with',
  'firms.intro': 'The studio’s address book. A project picks a firm from here instead of typing its name again.',
  'firms.search': 'Search a firm or a person',
  'firms.showArchived': 'Show archived',
  'firms.loading': 'Loading the directory…',
  'firms.explains': 'Firms you work with: engineers, contractors. Add them once, use them on every project.',
  'firms.empty.title': 'The address book is empty',
  'firms.empty': 'No firms yet. Add the first one above.',
  'firms.noMatches.title': 'Nothing found',
  'firms.noMatches': 'Nothing matches that.',
  'firms.add.name': 'Firm',
  'firms.add.action': 'Add',
  'firms.people': 'People',
  'firms.hide': 'Hide people',
  'firms.contacts': '{count, plural, one {# person} other {# people}}',
  'firms.noContacts': 'Nobody here yet.',
  'firms.archived': 'archived',
  'firms.rename': 'Rename',
  'firms.renameSave': 'Save the name',
  'firms.renameEverywhere': 'Renaming changes this firm on every project. Nothing already issued changes.',
  'firms.archive': 'Archive',
  'firms.restore': 'Restore',
  'firms.cancel': 'Cancel',
  'firms.primary': 'Main contact',
  'firms.primaryNone': 'Nobody in particular',
  'firms.address': 'Address',
  'firms.registrationId': 'IČO',
  'firms.taxId': 'DIČ',
  'firms.vatId': 'IČ DPH',
  'firms.phone': 'Company phone',
  'firms.email': 'Company e-mail',
  'firms.details.add': 'Add address and billing details',
  'firms.details.edit': 'Edit the details',
  'firms.details.save': 'Save the details',
  'firms.contact.name': 'Name',
  'firms.contact.phone': 'Phone',
  'firms.contact.email': 'E-mail',
  'firms.contact.emailHint': 'A record, not a channel. Nothing is sent from here.',
  'firms.contact.add': 'Add',
  'firms.contact.remove': 'Remove',
} as const;

export type StudioMessageKey = keyof typeof studioEn;
